import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { Gift, ShareNetwork, Handshake, CurrencyCircleDollar, WhatsappLogo, QrCode, CheckCircle, Users, ArrowRight, Rocket, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const STEPS = [
  { n: 1, icon: ShareNetwork, title: "Pegue seu link único", desc: "Ao criar sua conta, você recebe um código exclusivo (ex: JOAO1234) + link com QR pra compartilhar." },
  { n: 2, icon: WhatsappLogo, title: "Envie pro seu contato", desc: "Manda pelo WhatsApp com um clique. A mensagem já vem pronta explicando o app pra pessoa." },
  { n: 3, icon: CurrencyCircleDollar, title: "Amigo assina, os dois ganham", desc: "Quando ele fizer a primeira assinatura, você e ele ganham 50% OFF no próximo pagamento. Automático." },
  { n: 4, icon: Handshake, title: "Repita até 3 vezes", desc: "Cada conta pode ganhar 3 cupons (indicação 1/3, 2/3, 3/3). São 3 meses com metade do preço." },
];

const FAQ = [
  { q: "O que conta como \"indicação válida\"?", a: "Quando quem entrou pelo seu link efetivamente assina o plano pago (não só o trial de 7 dias). O cupom sai automático assim que Stripe confirma o pagamento." },
  { q: "Quando o cupom aparece na minha conta?", a: "Na hora. O sistema cria o cupom no Stripe e mostra ele em \"Meu Plano → Indique e ganhe\" imediatamente após o pagamento do indicado ser confirmado." },
  { q: "Empresa pode indicar motorista? E vice-versa?", a: "Sim, dos dois lados. Empresa indica motorista autônomo e ganha 50% OFF. Motorista indica empresa e também ganha. Cross-referral funciona." },
  { q: "O desconto é só uma vez ou continua?", a: "É 50% OFF UMA VEZ no seu próximo pagamento. Como você pode ter até 3 cupons, na prática pode ter 3 meses seguidos com metade do preço." },
  { q: "E se eu quiser indicar mais de 3 pessoas?", a: "Não tem limite pra indicar — só pra ganhar cupom. Depois das 3 indicações válidas, você já economizou até R$ 224/ano (empresa) ou R$ 89/ano (motorista) só indicando amigos." },
];

export default function ComoIndicar() {
  const { user } = useAuth();
  const ctaLink = user ? (user.role === "company" ? "/empresa/meu-plano" : user.role === "autonomo" ? "/autonomo/meu-plano" : "/motorista/meu-plano") : "/register";

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo size={40} /><div className="hidden sm:block"><div className="font-extrabold text-blue-900 text-sm leading-none" style={{ fontFamily: "Manrope" }}>ACERTO RÁPIDO</div><div className="text-[9px] tracking-[0.3em] text-blue-700/70">JVO</div></div></Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-700 hover:text-blue-700 hidden md:inline font-semibold">Home</Link>
            {!user && <Link to="/login" className="text-sm text-slate-700 hover:text-blue-700 font-semibold px-3">Entrar</Link>}
            <Link to={ctaLink} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-lg">{user ? "Meu link" : "Criar conta"}</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Badge className="bg-emerald-500 text-white mb-4 gap-1"><Gift size={12} weight="fill" /> PROGRAMA DE INDICAÇÃO</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
            Indique amigos e ganhe<br /><span className="text-emerald-600">50% OFF</span> na próxima mensalidade
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Cada motorista ou empresa que assinar pelo seu link te dá 50% de desconto no próximo pagamento. <strong>E o amigo também ganha 50%.</strong>
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={ctaLink}><Button data-testid="como-indicar-cta" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-2 h-12 px-6 text-base font-bold"><ShareNetwork size={20} weight="fill" /> {user ? "Ver meu link agora" : "Criar conta e pegar meu link"}</Button></Link>
          </div>
        </div>
      </section>

      {/* Quanto ganha */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Quanto você pode economizar</h2>
            <p className="text-slate-600 mt-2">Com só 3 indicações válidas, olha a economia:</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Card className="p-6 border-2 border-blue-200">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-bold">Motorista Autônomo</div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span>Mensalidade normal</span><span className="font-mono">R$ 25,90</span></div>
                <div className="flex justify-between text-emerald-700"><span>3 indicações × R$ 9,95 OFF</span><span className="font-mono font-bold">− R$ 29,85</span></div>
                <div className="border-t pt-2 mt-2 flex justify-between font-extrabold text-emerald-700"><span>Você economiza</span><span>R$ 29,85</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-3">Praticamente 1 mês e meio grátis com só 3 amigos.</p>
            </Card>
            <Card className="p-6 border-2 border-emerald-400 shadow-lg">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-bold">Empresa</div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span>Mensalidade normal</span><span className="font-mono">R$ 59,90</span></div>
                <div className="flex justify-between text-emerald-700"><span>3 indicações × R$ 24,95 OFF</span><span className="font-mono font-bold">− R$ 74,85</span></div>
                <div className="border-t pt-2 mt-2 flex justify-between font-extrabold text-emerald-700"><span>Você economiza</span><span>R$ 74,85</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-3">1 mês e meio de plano Empresa grátis. Só chamando 3 motoristas.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Passo-a-passo */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Como funciona — passo-a-passo</h2>
            <p className="text-slate-600 mt-2">Simples e automático. Sem burocracia.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {STEPS.map((s) => (
              <Card key={s.n} className="p-6 border-2 border-white bg-white shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center text-xl font-extrabold shrink-0" style={{ fontFamily: "Manrope" }}>{s.n}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon size={22} weight="duotone" className="text-emerald-600" />
                      <h3 className="font-extrabold text-lg" style={{ fontFamily: "Manrope" }}>{s.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600">{s.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Exemplos de mensagem */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Exemplos de mensagem</h2>
            <p className="text-slate-600 mt-2">O botão do WhatsApp no app já manda essas mensagens prontas — mas você pode adaptar.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 bg-emerald-50 border-2 border-emerald-200">
              <div className="flex items-center gap-2 mb-2"><WhatsappLogo size={20} weight="fill" className="text-emerald-600" /><span className="text-xs uppercase tracking-wider font-bold text-emerald-800">Empresa → Motorista</span></div>
              <p className="text-sm text-slate-800 leading-relaxed">
                "Oi! Uso o Acerto Rápido JVO pra gerenciar minha frota. Se você criar conta como motorista pelo meu link, ganha 50% OFF no 2º mês e eu também ganho. Testa 7 dias grátis: <span className="text-blue-700">acertorapidojvo.com.br/register?ref=EMPRESA1234</span>"
              </p>
            </Card>
            <Card className="p-5 bg-blue-50 border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2"><WhatsappLogo size={20} weight="fill" className="text-emerald-600" /><span className="text-xs uppercase tracking-wider font-bold text-blue-800">Motorista → Empresa</span></div>
              <p className="text-sm text-slate-800 leading-relaxed">
                "Boa noite! Estou usando um app chamado Acerto Rápido JVO pro meu acerto de viagens. Se o senhor cadastrar a empresa pelo meu link, o senhor ganha 50% OFF no 2º mês e eu também. Testa 7 dias grátis: <span className="text-blue-700">acertorapidojvo.com.br/register?ref=JOAO1234</span>"
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Perguntas frequentes</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group bg-white border-2 rounded-lg p-4 cursor-pointer">
                <summary className="font-bold text-slate-900 flex justify-between items-center list-none">
                  <span>{f.q}</span>
                  <span className="text-emerald-600 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="text-sm text-slate-600 mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Regras */}
      <section className="py-10 bg-amber-50">
        <div className="max-w-4xl mx-auto px-6 flex items-start gap-3">
          <WarningCircle size={24} weight="duotone" className="text-amber-600 shrink-0 mt-1" />
          <div className="text-sm text-amber-900">
            <strong>Regras do programa:</strong> Indicações só contam quando o indicado efetivamente paga (não vale só o trial). Limite de 3 cupons ganhos por conta. Cupons são de uso único, aplicados automaticamente no próximo pagamento. Programa pode ser alterado ou encerrado com 30 dias de aviso prévio.
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold" style={{ fontFamily: "Manrope" }}>Pronto pra ganhar 50% OFF?</h2>
          <p className="text-lg text-emerald-100 mt-3">Pegue seu link agora e mande pros amigos.</p>
          <Link to={ctaLink}><Button className="mt-6 bg-yellow-400 text-emerald-900 hover:bg-yellow-300 hover:text-emerald-900 font-extrabold h-12 px-8 text-base gap-2">
            <Rocket size={20} weight="fill" /> {user ? "Ver meu link" : "Criar conta grátis"}
          </Button></Link>
        </div>
      </section>
    </div>
  );
}
